import React from 'react';
import { Platform } from 'react-native';
import { Trans } from '@lingui/macro';
import styled, { css } from 'styled-components/native';

import RadioButton from '@src/components/Radio/RadioButton';
import Touchable from '@src/components/Touchable';
import { H2, H3, Subtitle2 } from '@src/components/Typography';
import { windowHeight, windowWidth } from '@src/configuration/constants';
import { persianNumber } from '@src/helpers';
import useLanguage from '@src/hooks/useLanguage';
import { PrimePlan } from '@src/types';

type Props = {
  active?: boolean;
  ActiveTab: number;
  data: PrimePlan;
  onPress?: () => void;
};

const PreviousAmountText = styled(Subtitle2)`
  text-decoration-line: line-through;
  margin-top: 10px;
  min-height: 20px;
`;
const EmptyView = styled(Subtitle2)`
  margin-top: 10px;
  min-height: 20px;
`;
const Title = styled(H2)<{ margin?: Boolean }>`
  font-weight: 400;
  margin-top: ${({ margin }) => (margin ? '2px' : '0px')};
`;
const TitleB = styled(H3)<{ margin?: Boolean }>`
  font-weight: 400;
  margin-top: ${({ margin }) => (margin ? '2px' : '0px')};
`;
const RadioContainer = styled.View`
  width: ${windowWidth * 0.35 - 13}px;
  align-items: center;
`;
const Container = styled.View<{ active?: boolean; ActiveTab: number }>`
  overflow: hidden;
  width: ${windowWidth * 0.3}px;
  min-height: ${windowHeight * 0.22}px;
  padding: 6.5px 0px 6.5px 0px;
  border-radius: ${({ theme }) => theme.roundness}px;
  border: ${({ active }) => (active ? '2.5px' : '1px')} solid
    ${({ theme, ActiveTab }) =>
      ActiveTab === 1 ? theme.colors.gold : theme.colors.primary};
  // elevation: ${({ active }) => (active ? 5 : 0)};
  shadow-color: ${({ theme }) => theme.colors.backdrop};
  shadow-offset: ${({ active }) => (active ? '0 1px' : '0 0')};
  shadow-opacity: ${({ active }) => (active ? 0.2 : 0)};
  shadow-radius: 1px;
`;

const Content = styled.View`
  align-items: center;
  justify-content: flex-start;
`;
const ViewDiscount = styled.View<{ ActiveTab?: number; Active?: boolean }>`
  border-radius: 4px;
  margin-top: 10px;
  background-color: ${({ theme, ActiveTab, Active }) =>
    Active
      ? ActiveTab === 1
        ? theme.colors.gold
        : theme.colors.success
      : theme.colors.transparent};
  padding: 4px;
  ${Platform.select({
    ios: css`
      min-height: 20px;
    `,
    web: css`
      min-height: 20px;
    `,
  })};
`;
const Famous = styled(Subtitle2)<{ active?: boolean; ActiveTab?: number }>`
  align-items: center;
  justify-content: center;
  text-align: center;
  margin: 0;
  font-weight: 700;
  color: ${({ theme, ActiveTab }) =>
    ActiveTab === 1 ? theme.colors.gold : theme.colors.success};
  opacity: ${({ active }) => (active ? 1 : 0)};
`;

const Component: React.FC<Props> = ({
  active,
  ActiveTab,
  data: { title, previousAmountText, discountText, amountText, mostFamous },
  onPress,
}) => {
  const language = useLanguage();
  let persianDiscount = discountText?.filter((e) => e.language === 'FA')[0];

  return (
    <Touchable onPress={onPress}>
      <Famous active={mostFamous} ActiveTab={ActiveTab}>
        <Trans>Favorite</Trans>
      </Famous>
      <Container active={active} ActiveTab={ActiveTab}>
        <Content>
          {windowWidth < 330 ? (
            <TitleB>{title?.[language] ?? ''}</TitleB>
          ) : (
            <Title>{title?.[language] ?? ''}</Title>
          )}
          {previousAmountText?.[language] ? (
            <PreviousAmountText>
              {previousAmountText?.[language]}
            </PreviousAmountText>
          ) : (
            <EmptyView>{'   '} </EmptyView>
          )}

          <Title margin={true}>{amountText?.[language] ?? ''}</Title>

          {persianDiscount?.text ? (
            <ViewDiscount ActiveTab={ActiveTab} Active={true}>
              <Subtitle2>{persianNumber(persianDiscount.text)}</Subtitle2>
            </ViewDiscount>
          ) : (
            <ViewDiscount>
              <Subtitle2>{'   '}</Subtitle2>
            </ViewDiscount>
          )}

          <RadioContainer>
            <RadioButton
              id="0"
              disabled={!active}
              ActiveTab={ActiveTab}
              onPress={onPress}
            />
          </RadioContainer>
        </Content>
      </Container>
    </Touchable>
  );
};

Component.displayName = 'PlanWithRadio';

export default React.memo(Component);
